import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    Card,
    List,
    Button,
    Modal,
    Form,
    Input,
    notification,
    Space,
    Tag,
    Checkbox
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";
import { v4 as uuidv4 } from "uuid";

const { confirm } = Modal;

const CategoryReasonManager = () => {
    const [categories, setCategories] = useState([]);
    const [reasonMaster, setReasonMaster] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(false);

    const [categoryModalVisible, setCategoryModalVisible] = useState(false);
    const [reasonModalVisible, setReasonModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingReason, setEditingReason] = useState(null);

    const [categoryForm] = Form.useForm();
    const [reasonForm] = Form.useForm();
    const [selectedReasons, setSelectedReasons] = useState([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/api/config`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
            });

            if (response.status === 200) {
                const reports = response.data.result?.report || [];

                // Add uid to categories & sub-categories
                const categoriesWithUid = reports.map(cat => ({
                    ...cat,
                    uid: `${cat.key}_${Date.now()}_${Math.random()}`, // category uid
                    sub_category: (cat.sub_category || []).map(r => ({
                        ...r,
                        uid: `${r.key}_${Date.now()}_${Math.random()}` // sub-category uid
                    }))
                }));

                setCategories(categoriesWithUid);

                // Collect unique reasons (with uid)
                const allReasons = [];
                categoriesWithUid.forEach(cat => {
                    (cat.sub_category || []).forEach(r => {
                        if (!allReasons.some(ar => ar.key === r.key)) {
                            allReasons.push(r);
                        }
                    });
                });

                setReasonMaster(allReasons);
            }
        } catch (error) {
            console.error(error);
            notification.error({ message: "Failed to fetch data" });
        } finally {
            setLoading(false);
        }
    };

    // ----- CATEGORY MODAL HANDLERS -----
    const openCategoryModal = (category = null) => {
        setEditingCategory(category);
        categoryForm.setFieldsValue(category || { category: "", key: "" });
        setCategoryModalVisible(true);
    };

    const saveCategory = async () => {
        try {
            const values = await categoryForm.validateFields();

            // 🔍 Check for duplicate key (only when creating, not editing)
            if (!editingCategory) {
                const exists = categories.some(c => c.key === values.key);
                if (exists) {
                    notification.error({
                        message: "Duplicate Key",
                        description: `The key "${values.key}" is already used by another category. Please choose a different key.`
                    });
                    return; // stop execution
                }
            }

            const payload = {
                categories: [
                    {
                        key: editingCategory ? editingCategory.key : values.key,
                        label: values.category,
                        reasons: !!editingCategory ? selectedReasons : []
                    }
                ]
            };

            await axios.patch(`${baseUrl}/api/report-management`, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
            });

            setCategoryModalVisible(false);
            notification.success({ message: "Category saved successfully" });

            // 🔄 refresh data
            fetchData();
        } catch (error) {
            console.error(error);
            notification.error({ message: "Failed to save category" });
        }
    };



    const deleteCategory = (category) => {
        confirm({
            title: `Are you sure you want to delete category "${category.category}"?`,
            icon: <ExclamationCircleOutlined />,
            okText: "Yes",
            okType: "danger",
            cancelText: "No",
            async onOk() {
                try {
                    await axios.delete(`${baseUrl}/api/report-management`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
                        data: { type: "category", key: category.key }
                    });

                    notification.success({ message: "Category deleted" });

                    // 🔄 refresh data
                    await fetchData();

                    // 🧹 reset selection if the deleted category was selected
                    setSelectedCategory(prev => {
                        if (prev?.key === category.key) {
                            return null; // clear selection
                        }
                        return prev;
                    });

                    setSelectedReasons([]); // clear reasons if no category is selected
                } catch (error) {
                    console.error(error);
                    notification.error({ message: "Failed to delete category" });
                }
            }
        });
    };


    const selectCategory = (category) => {
        setSelectedCategory(category);
        setSelectedReasons((category.sub_category || []).map(r => r.key)); // ✅ safe default
    };


    const openReasonModal = (reason = null) => {
        console.log(reason)
        setEditingReason(reason);
        reasonForm.resetFields(); // clear old values first
        reasonForm.setFieldsValue({
            label: reason?.label || "",
            key: reason?.key || "",
            description: reason?.subTitle || "", // 👈 force include description
            enableCustomMessage: reason?.enableCustomMessage || false,
            customMessageLabel: reason?.customMessageLabel || ""
        });
        setReasonModalVisible(true);
    };

    const saveReason = async () => {
        try {
            const values = await reasonForm.validateFields();

            // 🔍 Check for duplicate key (only when creating, not editing)
            if (!editingReason) {
                const exists = reasonMaster.some(r => r.key === values.key);
                if (exists) {
                    notification.error({
                        message: "Duplicate Key",
                        description: `The key "${values.key}" is already used by another reason. Please choose a different key.`
                    });
                    return; // stop execution
                }
            }

            const payload = {
                reason: {
                    key: editingReason ? editingReason.key : values.key,
                    label: values.label,
                    description: values.description || "",
                    assosiate_with: selectedCategory ? selectedCategory.key : ""
                }
            };

            await axios.patch(`${baseUrl}/api/report-management`, payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    "Content-Type": "application/json"
                }
            });

            setReasonModalVisible(false);
            notification.success({ message: "Reason saved successfully" });

            // 🔄 refresh data
            fetchData();
        } catch (error) {
            console.error(error);
            notification.error({ message: "Failed to save reason" });
        }
    };


    const deleteReason = (reason) => {
        confirm({
            title: `Are you sure you want to delete reason "${reason.label}"?`,
            icon: <ExclamationCircleOutlined />,
            okText: "Yes",
            okType: "danger",
            cancelText: "No",
            async onOk() {
                try {
                    await axios.delete(`${baseUrl}/api/report-management`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
                        data: { type: "reason", key: reason.key }
                    });

                    notification.success({ message: "Reason deleted" });

                    // 🔄 refresh data
                    fetchData();
                } catch (error) {
                    console.error(error);
                    notification.error({ message: "Failed to delete reason" });
                }
            }
        });
    };

    // ----- ASSIGN/UNASSIGN REASONS -----
    const handleReasonSelect = (checkedValues) => {
        setSelectedReasons(checkedValues);
    };

    const saveCategoryReasons = async () => {
        if (!selectedCategory) return;

        const payload = {
            categories: [
                {
                    key: selectedCategory.key,
                    label: selectedCategory.label || selectedCategory.category || "Unnamed Category",
                    reasons: selectedReasons
                }
            ]
        };

        try {
            await axios.patch(`${baseUrl}/api/report-management`, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
            });

            setCategories(prev =>
                prev.map(c =>
                    c.key === selectedCategory.key
                        ? { ...c, sub_category: reasonMaster.filter(r => selectedReasons.includes(r.key)) }
                        : c
                )
            );

            setSelectedCategory(prev => ({
                ...prev,
                sub_category: reasonMaster.filter(r => selectedReasons.includes(r.key))
            }));

            notification.success({ message: "Assigned reasons updated" });
            fetchData();


        } catch (error) {
            console.error(error);
            notification.error({ message: "Failed to update assigned reasons" });
        }
    };

    return (
        <Row gutter={16}>
            {/* --- Categories Pane --- */}
            <Col span={8}>
                <Card
                    title="Categories"
                    extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openCategoryModal()}>Add</Button>}
                >
                    <List
                        dataSource={categories}
                        renderItem={item => (
                            <List.Item
                                key={`cat-${item.key}`} // ✅ unique key
                                style={{ cursor: "pointer", backgroundColor: selectedCategory?.key === item.key ? "#f0f0f0" : "" }}
                                onClick={() => selectCategory(item)}
                                actions={[
                                    <Button icon={<EditOutlined />} onClick={() => openCategoryModal(item)} />,
                                    <Button icon={<DeleteOutlined />} danger onClick={() => deleteCategory(item)} />
                                ]}
                            >
                                {item.category}
                            </List.Item>
                        )}
                    />
                </Card>
            </Col>

            {/* --- Reasons Pane --- */}
            <Col span={16}>
                <Card
                    title={selectedCategory ? `Manage Reasons for "${selectedCategory.category}"` : "Select a category"}
                    extra={selectedCategory && (
                        <Button type="primary" onClick={saveCategoryReasons}>Assign Reason</Button>
                    )}
                >
                    {selectedCategory && (
                        <>
                            <Checkbox.Group
                                style={{ width: "100%" }}
                                value={selectedReasons}
                                onChange={handleReasonSelect}
                            >
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    {reasonMaster.map(r => (
                                        <Checkbox key={`reason-${r.key}`} value={r.key}> {/* ✅ unique key */}
                                            <Tag color="blue">{r.label}</Tag> - {r.description || "-"}
                                            <Space>
                                                <Button size="small" icon={<EditOutlined />} onClick={() => openReasonModal(r)} />
                                                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteReason(r)} />
                                            </Space>
                                        </Checkbox>
                                    ))}
                                </Space>
                            </Checkbox.Group>
                            <Button style={{ marginTop: 16 }} type="dashed" icon={<PlusOutlined />} onClick={() => openReasonModal()}>
                                Add New Reason
                            </Button>
                        </>
                    )}
                </Card>
            </Col>

            {/* ----- Modals ----- */}
            <Modal
                open={categoryModalVisible} // ✅ use `open` instead of `visible` if on AntD v5
                title={editingCategory ? "Edit Category" : "Add Category"}
                onCancel={() => setCategoryModalVisible(false)}
                onOk={saveCategory}
            >
                <Form form={categoryForm} layout="vertical">
                    <Form.Item label="Category Name" name="category" rules={[{ required: true, message: "Please enter category name" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="Category Key" name="key" rules={[{ required: true, message: "Please enter key" }]}>
                        <Input disabled={!!editingCategory} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={reasonModalVisible} // ✅ use `open` instead of `visible` if on AntD v5
                title={editingReason ? "Edit Reason" : "Add Reason"}
                onCancel={() => setReasonModalVisible(false)}
                onOk={saveReason}
            >
                <Form form={reasonForm} layout="vertical">
                    <Form.Item label="Label" name="label" rules={[{ required: true, message: "Please enter label" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="Key" name="key" rules={[{ required: true, message: "Please enter key" }]}>
                        <Input disabled={!!editingReason} />
                    </Form.Item>
                    <Form.Item label="Description" name="description">
                        <Input.TextArea />
                    </Form.Item>
                    {reasonForm.getFieldValue("enableCustomMessage") && (
                        <Form.Item label="Custom Message Label" name="customMessageLabel">
                            <Input />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </Row>
    );
};

export default CategoryReasonManager;
